/**
 * The AI Clinical Copilot controller.
 *
 * Owns the copilot session, the transcript, the doctor's decisions on extracted
 * information, and the post-call prescription flow — everything the panel
 * renders and nothing the call owns.
 *
 * The rule that governs every branch in this file: **the copilot may degrade,
 * but it may never propagate.** No failure here sets an error the call screen
 * reacts to, unmounts the stage, or blocks ending the consultation. When the
 * service is unreachable the hook reports `unavailable` and the doctor carries
 * on exactly as they would have without it (frontend "Failure handling").
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { copilotConfigured, isNotFound, isUnreachable } from "@/lib/api/aiAssistClient";
import { log } from "@/lib/logger";
import { copilotService } from "@/services/copilot/copilot.service";
import type {
  ClinicalAction,
  ClinicalBucket,
  ConsultationSummary,
  CopilotSession,
  PatientNotification,
  Prescription,
  TranscriptSegment,
} from "@/types/copilot";

/** While the call is live, new transcript arrives every few seconds. */
const TRANSCRIPT_POLL_MS = 3000;
/** While the service is processing the call-end, watch for `draft_ready`. */
const PROCESSING_POLL_MS = 2500;

/**
 * How often the copilot re-reads the consultation while it is running.
 *
 * Each pass is a model call over the whole transcript, so this is a cost dial
 * as much as a freshness one. 40s keeps the panel roughly a sentence or two
 * behind the conversation while a long consultation stays in the low tens of
 * calls rather than the hundreds.
 */
const AUTO_ANALYSE_INTERVAL_MS = 40_000;

/**
 * Don't re-read the transcript for a single "mhm".
 *
 * Re-running over two more segments produces the same extraction and bills for
 * it, so a pass waits until there is genuinely new material to read.
 */
const AUTO_ANALYSE_MIN_NEW_SEGMENTS = 3;

export interface CopilotController {
  /** False when the service is not configured for this deployment. */
  enabled: boolean;
  session: CopilotSession | null;
  segments: TranscriptSegment[];
  prescription: Prescription | null;
  summary: ConsultationSummary | null;
  notification: PatientNotification | null;

  /** True while the service could not be reached — a soft, recoverable state. */
  unavailable: boolean;
  connecting: boolean;
  analysing: boolean;
  /** The last non-fatal problem, for the panel's status line. */
  notice: string | null;

  open: () => void;
  refreshTranscript: () => void;
  analyse: () => void;
  loadContext: () => void;

  actOnItem: (
    bucket: ClinicalBucket,
    itemId: string,
    action: ClinicalAction,
    patch?: Record<string, string>,
  ) => Promise<void>;
  addItem: (bucket: ClinicalBucket, payload: Record<string, string>) => Promise<void>;

  /** Kicks off end-of-call processing. Safe to call after the call has ended. */
  finalize: () => void;
  retry: () => void;

  startPrescription: () => Promise<Prescription | null>;
  updatePrescription: (patch: Record<string, unknown>) => Promise<Prescription | null>;
  approvePrescription: (input: { signature: string; doctorName?: string }) => Promise<void>;
  issuePrescription: () => Promise<void>;
  setTranscriptState: (state: string) => void;
}

export function useCopilot(
  bookingId: string,
  { live = false }: { live?: boolean } = {},
): CopilotController {
  const [session, setSession] = useState<CopilotSession | null>(null);
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [summary, setSummary] = useState<ConsultationSummary | null>(null);
  const [notification, setNotification] = useState<PatientNotification | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const sessionIdRef = useRef<string | null>(null);
  const openingRef = useRef(false);

  sessionIdRef.current = session?.id ?? null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const apply = useCallback((next: CopilotSession) => {
    if (!mountedRef.current) return;
    setSession(next);
    setUnavailable(false);
  }, []);

  /**
   * One place where a copilot failure is turned into a soft state.
   *
   * `unavailable` is reserved for the service not answering at all. Anything
   * else becomes a notice the panel shows without changing what the doctor can
   * do.
   */
  const degrade = useCallback((error: unknown, context: string) => {
    if (!mountedRef.current) return;
    if (isUnreachable(error)) {
      setUnavailable(true);
      setNotice(null);
      return;
    }
    if (isNotFound(error)) return;
    const message = error instanceof Error ? error.message : "The AI copilot had a problem.";
    setNotice(message);
    log.warn("copilot", "degraded", { context, message });
  }, []);

  /** Opens or resumes the session. Idempotent — safe to call on every mount. */
  const open = useCallback(() => {
    if (!copilotConfigured || !bookingId || openingRef.current) return;
    openingRef.current = true;
    setConnecting(true);

    void (async () => {
      try {
        const { session: opened } = await copilotService.openSession(bookingId);
        apply(opened);

        // A resumed session may already have a transcript and a draft behind it.
        const [transcript, existing] = await Promise.allSettled([
          copilotService.getTranscript(opened.id),
          copilotService.getPrescription(opened.id),
        ]);
        if (!mountedRef.current) return;
        if (transcript.status === "fulfilled") setSegments(transcript.value.segments);
        if (existing.status === "fulfilled") setPrescription(existing.value.prescription);
      } catch (error) {
        degrade(error, "open");
      } finally {
        openingRef.current = false;
        if (mountedRef.current) setConnecting(false);
      }
    })();
  }, [apply, bookingId, degrade]);

  const refreshTranscript = useCallback(() => {
    const id = sessionIdRef.current;
    if (!id) return;
    void copilotService
      .getTranscript(id)
      .then((result) => {
        if (!mountedRef.current) return;
        setSegments(result.segments);
      })
      .catch((error) => degrade(error, "transcript"));
  }, [degrade]);

  /** Polls the transcript while the session is live. */
  useEffect(() => {
    if (!session || (session.status !== "active" && session.status !== "idle")) return undefined;
    const timer = setInterval(refreshTranscript, TRANSCRIPT_POLL_MS);
    return () => clearInterval(timer);
  }, [refreshTranscript, session]);

  /**
   * Watches for `draft_ready` while the service processes the call-end.
   *
   * Polling rather than a socket: this runs for a few seconds after a call, and
   * a websocket for that would be a second connection to keep alive for the
   * whole consultation to save a handful of requests at the end of it.
   */
  useEffect(() => {
    const id = session?.id;
    if (!id || session?.status !== "processing") return undefined;

    const timer = setInterval(() => {
      void copilotService
        .getSession(id)
        .then(({ session: next }) => {
          if (!mountedRef.current) return;
          apply(next);
          if (next.status === "draft_ready" || next.status === "failed") {
            void copilotService
              .getPrescription(id)
              .then(({ prescription: draft }) => mountedRef.current && setPrescription(draft))
              .catch(() => undefined);
          }
        })
        .catch((error) => degrade(error, "poll"));
    }, PROCESSING_POLL_MS);

    return () => clearInterval(timer);
  }, [apply, degrade, session?.id, session?.status]);

  const analyse = useCallback(() => {
    const id = sessionIdRef.current;
    if (!id || analysing) return;
    setAnalysing(true);
    setNotice(null);

    void copilotService
      .analyse(id)
      .then((result) => {
        if (!mountedRef.current) return;
        apply(result.session);
        // A degraded pass is a 200: the panel keeps working and says why it did
        // not grow.
        if (!result.ai.ok) setNotice(result.ai.message);
      })
      .catch((error) => degrade(error, "analyse"))
      .finally(() => mountedRef.current && setAnalysing(false));
  }, [analysing, apply, degrade]);

  /*
   * Continuous analysis.
   *
   * The doctor is consulting, not operating a panel, so the copilot re-reads
   * the transcript on its own for as long as the call is live. Everything the
   * interval needs is read through refs, so the timer is created once when the
   * call goes live rather than being torn down and rebuilt on every transcript
   * poll.
   */
  const segmentCountRef = useRef(0);
  const analysingRef = useRef(false);
  const aiAvailableRef = useRef(false);
  const lastAnalysedCountRef = useRef(0);
  const analyseRef = useRef<() => void>(() => {});

  segmentCountRef.current = segments.length;
  analysingRef.current = analysing;
  aiAvailableRef.current = session?.aiAvailable ?? false;
  analyseRef.current = analyse;

  useEffect(() => {
    if (!live) return undefined;

    const tick = () => {
      if (!sessionIdRef.current || !aiAvailableRef.current) return;
      // A pass already running, or nothing new said since the last one.
      if (analysingRef.current) return;
      if (segmentCountRef.current - lastAnalysedCountRef.current < AUTO_ANALYSE_MIN_NEW_SEGMENTS) {
        return;
      }
      lastAnalysedCountRef.current = segmentCountRef.current;
      analyseRef.current();
    };

    const timer = setInterval(tick, AUTO_ANALYSE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [live]);

  // A new call starts reading from scratch.
  useEffect(() => {
    if (!live) lastAnalysedCountRef.current = 0;
  }, [live]);

  const loadContext = useCallback(() => {
    const id = sessionIdRef.current;
    if (!id) return;
    void copilotService
      .getContext(id)
      .then(({ context }) => {
        if (!mountedRef.current) return;
        setSession((current) => (current ? { ...current, context } : current));
      })
      .catch((error) => degrade(error, "context"));
  }, [degrade]);

  const actOnItem = useCallback(
    async (
      bucket: ClinicalBucket,
      itemId: string,
      action: ClinicalAction,
      patch?: Record<string, string>,
    ) => {
      const id = sessionIdRef.current;
      if (!id) return;
      try {
        const { session: next } = await copilotService.actOnClinicalItem(
          id,
          bucket,
          itemId,
          action,
          patch,
        );
        apply(next);
      } catch (error) {
        degrade(error, "clinicalAction");
      }
    },
    [apply, degrade],
  );

  const addItem = useCallback(
    async (bucket: ClinicalBucket, payload: Record<string, string>) => {
      const id = sessionIdRef.current;
      if (!id) return;
      try {
        const { session: next } = await copilotService.addClinicalItem(id, bucket, payload);
        apply(next);
      } catch (error) {
        degrade(error, "addItem");
      }
    },
    [apply, degrade],
  );

  const setTranscriptState = useCallback(
    (state: string) => {
      const id = sessionIdRef.current;
      if (!id) return;
      void copilotService
        .setState(id, { transcriptState: state })
        .then(({ session: next }) => mountedRef.current && apply(next))
        .catch(() => undefined);
    },
    [apply],
  );

  /**
   * Triggers end-of-call processing.
   *
   * Called after the call has already ended and the booking has already been
   * completed by the backend, so there is nothing left for it to interfere
   * with. A failure is swallowed: the doctor can still write the prescription
   * by hand, which is exactly what §16 requires.
   */
  const finalize = useCallback(() => {
    const id = sessionIdRef.current;
    if (!id) return;
    void copilotService
      .finalize(id)
      .then(() => {
        if (!mountedRef.current) return;
        setSession((current) => (current ? { ...current, status: "processing" } : current));
      })
      .catch((error) => degrade(error, "finalize"));
  }, [degrade]);

  const retry = useCallback(() => {
    const id = sessionIdRef.current;
    if (!id) return;
    setNotice(null);
    void copilotService
      .retry(id)
      .then(() => {
        if (!mountedRef.current) return;
        setSession((current) => (current ? { ...current, status: "processing" } : current));
      })
      .catch((error) => degrade(error, "retry"));
  }, [degrade]);

  const startPrescription = useCallback(async () => {
    const id = sessionIdRef.current;
    if (!id) return null;
    try {
      const { prescription: draft } = await copilotService.startPrescription(id);
      const reviewed = await copilotService.markUnderReview(draft.id).catch(() => ({
        prescription: draft,
      }));
      if (mountedRef.current) setPrescription(reviewed.prescription);
      return reviewed.prescription;
    } catch (error) {
      degrade(error, "startPrescription");
      return null;
    }
  }, [degrade]);

  const updatePrescription = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!prescription) return null;
      try {
        const { prescription: next } = await copilotService.updatePrescription(
          prescription.id,
          patch,
        );
        if (mountedRef.current) setPrescription(next);
        return next;
      } catch (error) {
        degrade(error, "updatePrescription");
        return null;
      }
    },
    [degrade, prescription],
  );

  const approvePrescription = useCallback(
    async (input: { signature: string; doctorName?: string }) => {
      if (!prescription) return;
      try {
        const { prescription: next } = await copilotService.approve(prescription.id, input);
        if (mountedRef.current) setPrescription(next);
      } catch (error) {
        // A refused approval is the doctor's to see and fix — a missing
        // diagnosis, or content that changed after signing — so it surfaces
        // rather than degrading quietly.
        degrade(error, "approve");
        throw error;
      }
    },
    [degrade, prescription],
  );

  const issuePrescription = useCallback(async () => {
    if (!prescription) return;
    try {
      const result = await copilotService.issue(prescription.id);
      if (!mountedRef.current) return;
      setPrescription(result.prescription);
      setSummary(result.summary);
      setNotification(result.notification);
    } catch (error) {
      degrade(error, "issue");
      throw error;
    }
  }, [degrade, prescription]);

  return {
    enabled: copilotConfigured,
    session,
    segments,
    prescription,
    summary,
    notification,
    unavailable,
    connecting,
    analysing,
    notice,
    open,
    refreshTranscript,
    analyse,
    loadContext,
    actOnItem,
    addItem,
    finalize,
    retry,
    startPrescription,
    updatePrescription,
    approvePrescription,
    issuePrescription,
    setTranscriptState,
  };
}
